import { AddressAutocomplete, applySelectedAddress } from '../components/AddressAutocomplete';
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card, FormInput, Button } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { adminApi } from '../services/api';
import { apiErrorText } from '../utils/apiError';
import type { AdminUser } from '../types';

type SortDirection = 'asc' | 'desc';
type SortField = 'companyNumber' | 'companyName' | 'fullName' | 'email' | 'phone' | 'address' | 'postalCode' | 'city' | 'createdAt';

export function AdminAdminList() {
  const { t } = useTranslation();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Search & sort
  const [sortField, setSortField] = useState<SortField>('fullName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Column filters
  const [filters, setFilters] = useState<Record<string, string>>({});

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState({ fullName: '', phone: '', address: '', postalCode: '', city: '', role: 'admin' as 'admin' | 'support' });
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ fullName: '', email: '', phone: '', address: '', postalCode: '', city: '', role: 'admin' as 'admin' | 'support' });
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      const data = await adminApi.getAllAdmins();
      setAdmins(data);
    } catch (error) {
      console.error('Failed to load admins:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const columns: { field: SortField; label: string }[] = [
    { field: 'companyNumber', label: t('adminAdminList.columns.companyNumber') },
    { field: 'companyName', label: t('adminAdminList.columns.companyName') },
    { field: 'fullName', label: t('adminAdminList.columns.fullName') },
    { field: 'email', label: t('adminAdminList.columns.email') },
    { field: 'phone', label: t('adminAdminList.columns.phone') },
    { field: 'address', label: t('adminAdminList.columns.address') },
    { field: 'postalCode', label: t('adminAdminList.columns.postalCode') },
    { field: 'city', label: t('adminAdminList.columns.city') },
    { field: 'createdAt', label: t('adminAdminList.columns.createdAt') }
  ];

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const filteredAndSorted = useMemo(() => {
    let result = [...admins];

    // Column filters
    Object.entries(filters).forEach(([field, value]) => {
      if (!value) return;
      const v = value.toLowerCase();
      result = result.filter(a => {
        const cellValue = String((a as any)[field] || '').toLowerCase();
        return cellValue.includes(v);
      });
    });

    // Sort
    result.sort((a, b) => {
      const aVal = String((a as any)[sortField] || '').toLowerCase();
      const bVal = String((b as any)[sortField] || '').toLowerCase();
      const cmp = aVal.localeCompare(bVal, 'sv');
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [admins, filters, sortField, sortDirection]);

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return <i className="feather-chevrons-up-down ms-1" style={{ opacity: 0.3, fontSize: '12px' }}></i>;
    return sortDirection === 'asc'
      ? <i className="feather-chevron-up ms-1" style={{ fontSize: '12px' }}></i>
      : <i className="feather-chevron-down ms-1" style={{ fontSize: '12px' }}></i>;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('sv-SE');
  };

  const getCellValue = (admin: AdminUser, field: SortField): string => {
    if (field === 'createdAt') return formatDate(admin.createdAt);
    return String((admin as any)[field] || '');
  };

  // --- Edit ---
  const openEdit = (admin: AdminUser) => {
    setEditingAdmin(admin);
    setEditForm({
      fullName: admin.fullName || '',
      phone: admin.phone || '',
      address: admin.address || '',
      postalCode: admin.postalCode || '',
      city: admin.city || '',
      role: admin.isSupport ? 'support' : 'admin'
    });
    setEditErrors({});
    setShowEditModal(true);
  };

  const validateEdit = () => {
    const errors: Record<string, string> = {};
    if (!editForm.fullName.trim()) errors.fullName = t('validation.required');
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveEdit = async () => {
    if (!validateEdit() || !editingAdmin) return;
    setIsSaving(true);
    try {
      const { role, fullName: _fullName, ...profileData } = editForm;
      await adminApi.updateAdmin(editingAdmin.id, {
        ...profileData,
        isAdmin: role === 'admin',
        isSupport: role === 'support'
      });
      setMessage({ type: 'success', text: t('adminAdminList.saveSuccess') });
      setShowEditModal(false);
      loadAdmins();
    } catch (error: unknown) {
      setMessage({ type: 'error', text: apiErrorText(t, error, 'adminAdminList.saveError') });
    } finally {
      setIsSaving(false);
    }
  };

  // --- Create ---
  const openCreate = () => {
    setCreateForm({ fullName: '', email: '', phone: '', address: '', postalCode: '', city: '', role: 'admin' });
    setCreateErrors({});
    setShowCreateModal(true);
  };

  const validateCreate = () => {
    const errors: Record<string, string> = {};
    if (!createForm.email.trim()) {
      errors.email = t('validation.required');
    } else if (!/\S+@\S+\.\S+/.test(createForm.email)) {
      errors.email = t('validation.email');
    }
    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateCreate()) return;
    setIsCreating(true);
    try {
      await adminApi.createAdmin({
        email: createForm.email,
        isSupport: createForm.role === 'support'
      });
      setMessage({ type: 'success', text: t('adminAdminList.createSuccess') });
      setShowCreateModal(false);
      loadAdmins();
    } catch (error: unknown) {
      setMessage({ type: 'error', text: apiErrorText(t, error, 'adminAdminList.createError') });
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('adminAdminList.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('adminAdminList.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('adminAdminList.description')}</p>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-4`} role="alert">
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      <Card>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex gap-2">
            <Button onClick={openCreate}>
              <i className="feather-plus"></i>
              <span className="d-none d-sm-inline ms-2">{t('adminAdminList.createAdmin')}</span>
            </Button>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">{t('adminAdminList.total')}: {filteredAndSorted.length}</span>
          </div>
        </div>

        {/* Desktop table */}
        <div className="d-none d-md-block">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  {columns.map(col => (
                    <th
                      key={col.field}
                      onClick={() => handleSort(col.field)}
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      {col.label}
                      {renderSortIcon(col.field)}
                    </th>
                  ))}
                  <th style={{ width: '1%' }}></th>
                </tr>
                <tr>
                  {columns.map(col => (
                    <th key={`filter-${col.field}`} className="p-1">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={t('customers.search')}
                        value={filters[col.field] || ''}
                        onChange={(e) => handleFilterChange(col.field, e.target.value)}
                      />
                    </th>
                  ))}
                  <th className="p-1"></th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSorted.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="text-center py-4 text-muted">
                      {t('adminAdminList.noResults')}
                    </td>
                  </tr>
                ) : (
                  filteredAndSorted.map(admin => (
                    <tr key={admin.id}>
                      {columns.map(col => (
                        <td key={col.field} className={col.field === 'email' ? 'text-truncate' : ''} style={col.field === 'email' ? { maxWidth: '200px' } : undefined}>
                          {getCellValue(admin, col.field)}
                        </td>
                      ))}
                      <td>
                        <button
                          className="btn btn-sm btn-light-brand"
                          onClick={() => openEdit(admin)}
                          title={t('adminAdminList.editAdmin')}
                        >
                          <i className="feather-edit"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile cards */}
        <div className="d-md-none">
          {filteredAndSorted.length === 0 ? (
            <div className="text-center py-4 text-muted">{t('adminAdminList.noResults')}</div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {filteredAndSorted.map(admin => (
                <div
                  key={admin.id}
                  className="border rounded p-3"
                  onClick={() => openEdit(admin)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <div className="fw-bold">{admin.fullName}</div>
                      <div className="text-muted small">{admin.email}</div>
                      {admin.companyName && (
                        <div className="text-muted small"><i className="feather-briefcase me-1"></i>{admin.companyNumber} — {admin.companyName}</div>
                      )}
                    </div>
                    <button className="btn btn-sm btn-light-brand">
                      <i className="feather-edit"></i>
                    </button>
                  </div>
                  {admin.phone && (
                    <div className="text-muted small"><i className="feather-phone me-1"></i>{admin.phone}</div>
                  )}
                  {admin.city && (
                    <div className="text-muted small"><i className="feather-map-pin me-1"></i>{[admin.address, admin.postalCode, admin.city].filter(Boolean).join(', ')}</div>
                  )}
                  <div className="text-muted small mt-1"><i className="feather-calendar me-1"></i>{formatDate(admin.createdAt)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Edit Modal */}
      {showEditModal && editingAdmin && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} onClick={() => setShowEditModal(false)}>
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('adminAdminList.editTitle')}</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="fullName"
                      disabled
                      label={t('adminAdminList.fields.fullName')}
                      value={editForm.fullName}
                      onChange={(e) => setEditForm(prev => ({ ...prev, fullName: e.target.value }))}
                      error={editErrors.fullName}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="email"
                      label={t('adminAdminList.fields.email')}
                      value={editingAdmin.email}
                      disabled
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="editStaffRole">
                      {t('adminAdminList.fields.role')}
                    </label>
                    <select
                      id="editStaffRole"
                      className="form-select"
                      value={editForm.role}
                      onChange={(event) => setEditForm(prev => ({ ...prev, role: event.target.value as 'admin' | 'support' }))}
                    >
                      <option value="admin">{t('adminAdminList.roles.admin')}</option>
                      <option value="support">{t('adminAdminList.roles.support')}</option>
                    </select>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <FormInput
                      type="tel"
                      name="phone"
                      label={t('adminAdminList.fields.phone')}
                      value={editForm.phone}
                      onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <AddressAutocomplete
                      name="address"
                      onAddressSelect={address => setEditForm(previous => applySelectedAddress(previous, address))}
                      label={t('adminAdminList.fields.address')}
                      value={editForm.address}
                      onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="postalCode"
                      label={t('adminAdminList.fields.postalCode')}
                      value={editForm.postalCode}
                      onChange={(e) => setEditForm(prev => ({ ...prev, postalCode: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="city"
                      label={t('adminAdminList.fields.city')}
                      value={editForm.city}
                      onChange={(e) => setEditForm(prev => ({ ...prev, city: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer d-flex justify-content-between">
                <div className="d-flex gap-2">
                  <Button variant="light-brand" onClick={() => setShowEditModal(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button onClick={handleSaveEdit} isLoading={isSaving}>
                    <i className="feather-save me-2"></i>
                    {t('common.save')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} onClick={() => setShowCreateModal(false)}>
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('adminAdminList.createTitle')}</h5>
                <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted">{t('bankid.existingAccountRequired')}</p>
                <FormInput
                  name="email"
                  type="email"
                  label={t('adminAdminList.fields.email')}
                  value={createForm.email}
                  onChange={event => setCreateForm(previous => ({ ...previous, email: event.target.value }))}
                  error={createErrors.email}
                  required
                />
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="createStaffRole">
                      {t('adminAdminList.fields.role')}
                    </label>
                    <select
                      id="createStaffRole"
                      className="form-select"
                      value={createForm.role}
                      onChange={(event) => setCreateForm(prev => ({ ...prev, role: event.target.value as 'admin' | 'support' }))}
                    >
                      <option value="admin">{t('adminAdminList.roles.admin')}</option>
                      <option value="support">{t('adminAdminList.roles.support')}</option>
                    </select>
                  </div>
                </div>

              </div>
              <div className="modal-footer">
                <Button variant="light-brand" onClick={() => setShowCreateModal(false)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleCreate} isLoading={isCreating}>
                  <i className="feather-user-plus me-2"></i>
                  {t('adminAdminList.createAdmin')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
